import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api/client';
import { Anomaly, Report } from '../types';
import SeverityBadge from '../components/SeverityBadge';
import CooldownButton from '../components/CooldownButton';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const AppDetail: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(() => {
    if (!appId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<Anomaly[]>(`/anomalies?appId=${appId}`),
      api.get<Report>(`/reports/${appId}`).catch(() => null),
    ])
      .then(([anomalyData, reportData]) => {
        setAnomalies(anomalyData);
        setReport(reportData);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [appId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (cooldown > 0) {
      timerRef.current = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [cooldown]);

  const handleRefresh = async () => {
    if (!appId) return;
    setRefreshMsg(null);
    try {
      const result = await api.post<{ appId: string; message: string }>(`/reports/${appId}/refresh`);
      setRefreshMsg(result.message);
    } catch (err: any) {
      if (err.retryAfter) {
        setCooldown(err.retryAfter);
      }
      setRefreshMsg(err.message);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/" className="text-sm text-slate-500 hover:text-brand-700">← Back to dashboard</Link>
        <h1 className="mt-2">{appId}</h1>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
          <h2>AI Analysis Report</h2>
          <CooldownButton
            label="Refresh Analysis"
            onClick={handleRefresh}
            cooldownSeconds={cooldown}
          />
        </div>
        {refreshMsg && (
          <div className="px-5 pt-3 text-xs text-slate-500">{refreshMsg}</div>
        )}
        {report ? (
          <div className="p-5">
            <div className="text-xs text-slate-500 mb-3">
              Generated {new Date(report.generatedAt).toLocaleString()} · Triggered by {report.triggeredBy} · {report.anomalyCount} anomalies
            </div>
            <div
              className="text-sm leading-relaxed text-slate-800 [&_h1]:text-lg [&_h2]:text-base [&_h1]:font-semibold [&_h2]:font-semibold [&_h1]:mt-4 [&_h2]:mt-4 [&_h1]:mb-2 [&_h2]:mb-2 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_code]:font-mono [&_code]:text-xs [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:rounded"
              dangerouslySetInnerHTML={{ __html: report.reportBody }}
            />
          </div>
        ) : (
          <div className="p-5 text-sm text-slate-500">No report available for this application.</div>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2>Recent Anomalies</h2>
          <span className="text-xs text-slate-500">{anomalies.length} total</span>
        </div>
        {anomalies.length === 0 ? (
          <div className="p-5 text-sm text-slate-500">No anomalies recorded for this application.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Stack</th>
                  <th>Account</th>
                  <th>Region</th>
                  <th>Priority</th>
                  <th>Log Group</th>
                  <th>Reported</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {anomalies.map((a) => (
                  <tr
                    key={a.compositeKey}
                    onClick={() => navigate(`/anomalies/${a.accountId}/${a.region}/${a.anomalyId}`)}
                    className="cursor-pointer"
                  >
                    <td className="font-medium text-slate-900">{a.stackId}</td>
                    <td className="font-mono text-xs text-slate-600">{a.accountId}</td>
                    <td className="text-slate-600">{a.region}</td>
                    <td><SeverityBadge priority={a.priority} /></td>
                    <td className="font-mono text-xs text-slate-600 truncate max-w-xs">{a.logGroupName}</td>
                    <td className="text-slate-600 whitespace-nowrap">{new Date(a.reportedAt).toLocaleString()}</td>
                    <td>
                      {a.ackState ? (
                        <span className="badge-ok">Acked</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppDetail;
